import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { X, Mail, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { isValidEmail } from '@/utils/validation';
import { showError, showSuccess } from '@/utils/alert';

const RESEND_COOLDOWN_SECONDS = 60;

interface SignupPageProps {
  onClose?: () => void;
}

/* ─── Step Indicator ─── */
const StepIndicator: React.FC<{ currentStep: number }> = ({ currentStep }) => {
  const steps = [1, 2];
  return (
    <div className="flex items-center justify-center gap-0 my-8">
      {steps.map((step, i) => (
        <React.Fragment key={step}>
          {/* Dot / checkmark */}
          <div className="flex items-center justify-center">
            {currentStep > step ? (
              /* Completed — green checkmark circle */
              <div className="w-6 h-6 rounded-full bg-[#a9f600] flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
            ) : currentStep === step ? (
              /* Active — green filled dot */
              <div className="w-3 h-3 rounded-full bg-[#a9f600]" />
            ) : (
              /* Inactive — grey dot */
              <div className="w-3 h-3 rounded-full bg-[#d0d5dd]" />
            )}
          </div>
          {/* Connector line */}
          {i < steps.length - 1 && (
            <div className="w-24 h-[2px] mx-1">
              <div
                className="h-full rounded-full transition-colors duration-300"
                style={{
                  background: currentStep > step ? '#a9f600' : '#e4e7ec',
                }}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export const SignupPage: React.FC<SignupPageProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) {
      clearInterval(cooldownRef.current);
      return;
    }
    cooldownRef.current = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown > 0]);

  const emailFromUrl = searchParams.get('email');
  const title = emailFromUrl ? t('signup.titleFromLogin') : t('signup.title');
  const subtitle = emailFromUrl ? t('signup.subtitleFromLogin') : t('signup.subtitle');

  const canProceed = step === 1 && firstName.trim() !== '' && email.trim() !== '';

  const submitStep2 = async () => {
    if (!firstName.trim() || !email.trim()) return;
    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: trimmedEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/cowork`,
          data: { full_name: firstName.trim() },
        },
      });
      if (error) {
        if (error.status === 429) throw { isRateLimit: true };
        throw error;
      }
      setStep(2);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      if (err?.isRateLimit || err?.status === 429) {
        showError(t('signup.errorRateLimit'));
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        showError(t('signup.errorGeneric'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = useCallback(() => {
    if (step === 1 && canProceed) {
      submitStep2();
    }
  }, [step, canProceed]);

  useEffect(() => {
    if (step !== 1) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && canProceed) {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, canProceed, handleNext]);

  useEffect(() => {
    if (step === 1 && emailFromUrl) {
      const id = setTimeout(() => {
        firstNameInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(id);
    }
  }, [step, emailFromUrl]);

  const onSubmitStep = useCallback(() => {
    if (step === 1 && canProceed && !isSubmitting) handleNext();
  }, [step, canProceed, isSubmitting, handleNext]);

  const handleResend = () => {
    if (cooldown > 0) return;
    if (!firstName.trim() || !email.trim()) return;
    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setIsSubmitting(true);
    supabase.auth.signInWithOtp({
      email: trimmedEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/cowork`,
        data: { full_name: firstName.trim() },
      },
    })
      .then(({ error }) => {
        if (error?.status === 429) {
          showError(t('signup.errorRateLimit'));
        } else if (error) {
          showError(t('signup.errorGeneric'));
        } else {
          showSuccess(t('signup.emailResent'));
        }
        setCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch(() => showError(t('signup.errorGeneric')))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-auto"
      style={{ background: '#f8f9fa', animation: 'signupFadeIn 300ms ease-out' }}
    >
      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-white transition-all z-10"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Centered content */}
      <div
        className="w-full max-w-[480px] mx-4"
        style={{ animation: 'signupContentIn 500ms ease-out' }}
      >
        {/* Logo — raw, above the card */}
        <div className="flex justify-center mb-8">
          <Link to="/login">
            <img src="/omni-logo-dark.png" alt="Omni Ad" className="h-8 w-auto" />
          </Link>
        </div>

        {/* White card */}
        <div
          className="bg-white rounded-[24px] px-10 py-10"
          style={{
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 24px rgba(0,0,0,0.06), 0 12px 48px rgba(0,0,0,0.04)',
          }}
        >
          {/* Header — consistent across steps */}
          <div className="text-center">
            <h1
              className="text-[#101828] text-[26px] mb-2"
              style={{ fontFamily: 'Instrument Sans, sans-serif', fontWeight: 600 }}
            >
              {title}
            </h1>
            <p className="text-[#667085] text-[15px] leading-relaxed">{subtitle}</p>
          </div>

          {/* Step indicator */}
          <StepIndicator currentStep={step} />

          {/* Step content */}
          <div style={{ animation: 'signupStepIn 300ms ease-out' }} key={step}>
            {step === 1 && (
              <StepTwo
                firstName={firstName}
                email={email}
                onFirstNameChange={setFirstName}
                onEmailChange={setEmail}
                focusedField={focusedField}
                onFocus={setFocusedField}
                emailInputRef={emailInputRef}
                firstNameInputRef={firstNameInputRef}
                onSubmitStep={onSubmitStep}
              />
            )}
            {step === 2 && (
              <StepThree email={email} onResend={handleResend} isResending={isSubmitting} cooldown={cooldown} />
            )}
          </div>

          {/* Next button — step 1 only */}
          {step < 2 && (
            <button
              onClick={handleNext}
              disabled={!canProceed || isSubmitting}
              className="w-full py-3.5 rounded-xl text-white text-[13px] tracking-[0.08em] transition-all active:scale-[0.98] flex items-center justify-center mt-8 disabled:opacity-40"
              style={{
                fontWeight: 600,
                textTransform: 'uppercase' as const,
                background: canProceed && !isSubmitting ? '#be43ff' : '#d0d5dd',
                boxShadow: canProceed && !isSubmitting ? '0 4px 16px rgba(190,67,255,0.25)' : 'none',
              }}
            >
              {step === 1 && isSubmitting ? t('signup.sending') : t('signup.next')}
            </button>
          )}
        </div>
        <p className="text-center text-[14px] text-[#667085] mt-4">
          {t('signup.hasAccount')}{' '}
          <Link to="/login" className="text-[#be43ff] hover:text-[#a830e0]" style={{ fontWeight: 600 }}>
            {t('signup.logIn')}
          </Link>
        </p>
      </div>

      <style>{`
        @keyframes signupFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes signupContentIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes signupStepIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Step 1 — Contact Data (name + email)
   ═══════════════════════════════════════════════ */
interface StepTwoProps {
  firstName: string;
  email: string;
  onFirstNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  focusedField: string | null;
  onFocus: (v: string | null) => void;
  emailInputRef: React.RefObject<HTMLInputElement | null>;
  firstNameInputRef: React.RefObject<HTMLInputElement | null>;
  onSubmitStep: () => void;
}
const StepTwo: React.FC<StepTwoProps> = ({
  firstName, email, onFirstNameChange, onEmailChange, focusedField, onFocus,
  emailInputRef, firstNameInputRef, onSubmitStep,
}) => {
  const { t } = useTranslation();
  return (
  <div>
    <h2 className="text-[#101828] text-[16px] mb-5" style={{ fontWeight: 600 }}>
      {t('signup.step2Title')}
    </h2>
    <div className="space-y-5">
      {/* First name */}
      <div>
        <label className="block text-[13px] text-[#344054] mb-1.5" style={{ fontWeight: 500 }}>
          {t('signup.firstNameLabel')} <span className="text-[#be43ff]">*</span>
        </label>
        <input
          ref={firstNameInputRef}
          type="text"
          value={firstName}
          onChange={(e) => onFirstNameChange(e.target.value)}
          onFocus={() => onFocus('firstName')}
          onBlur={() => onFocus(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              emailInputRef.current?.focus();
            }
          }}
          placeholder="Enter your name"
          className="w-full px-4 py-3 bg-white text-[15px] text-[#101828] placeholder:text-[#d0d5dd] outline-none rounded-xl border transition-all duration-200"
          style={{
            borderColor: focusedField === 'firstName' ? '#be43ff' : '#e4e7ec',
            boxShadow: focusedField === 'firstName' ? '0 0 0 3px rgba(190,67,255,0.08)' : 'none',
          }}
          required
        />
      </div>

      {/* Email */}
      <div>
        <label className="block text-[13px] text-[#344054] mb-1.5" style={{ fontWeight: 500 }}>
          {t('signup.emailLabel')} <span className="text-[#be43ff]">*</span>
        </label>
        <input
          ref={emailInputRef}
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onFocus={() => onFocus('email')}
          onBlur={() => onFocus(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmitStep();
            }
          }}
          placeholder="example@gmail.com"
          className="w-full px-4 py-3 bg-white text-[15px] text-[#101828] placeholder:text-[#d0d5dd] outline-none rounded-xl border transition-all duration-200"
          style={{
            borderColor: focusedField === 'email' ? '#be43ff' : '#e4e7ec',
            boxShadow: focusedField === 'email' ? '0 0 0 3px rgba(190,67,255,0.08)' : 'none',
          }}
          required
        />
      </div>
    </div>
  </div>
  );
};

/* ═══════════════════════════════════════════════
   Step 2 — Email Verification
   ═══════════════════════════════════════════════ */
interface StepThreeProps {
  email: string;
  onResend: () => void;
  isResending: boolean;
  cooldown: number;
}
const StepThree: React.FC<StepThreeProps> = ({ email: _email, onResend, isResending, cooldown }) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="rounded-xl border border-[#e4e7ec] px-8 py-10 text-center">
        <div className="flex justify-center mb-5">
          <Mail className="w-8 h-8 text-[#101828]" />
        </div>

        <h2 className="text-[#101828] text-[16px] mb-3" style={{ fontWeight: 600 }}>
          {t('signup.verifyEmailTitle')}
        </h2>
        <p className="text-[#667085] text-[14px] leading-relaxed max-w-[300px] mx-auto mb-5">
          {t('signup.verifyEmailSubtitle')}
        </p>
        <p className="text-[#667085] text-[13px]">
          {t('signup.noEmailOrExpired')}{' '}
          <button
            type="button"
            onClick={onResend}
            disabled={isResending || cooldown > 0}
            className="text-[#101828] underline underline-offset-2 hover:text-[#be43ff] transition-colors disabled:opacity-70"
            style={{ fontWeight: 600 }}
          >
            {cooldown > 0 ? `${t('signup.requestNewLink')} (${cooldown}s)` : t('signup.requestNewLink')}
          </button>
        </p>
      </div>
    </div>
  );
};
